
import Link from '@/components/compat/Link';
import { useMemo, useState } from 'react';
import { ExternalLink, History, Pencil, Plus, RefreshCw, Search, Trash2 } from 'lucide-react';
import { PainelConfirmDialog } from '@/components/painel/PainelFeedbackDialog';
import { PainelContentHistoryModal } from '@/components/painel/PainelContentHistoryModal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { getAceStatusLabel } from '@/lib/aces-data';
import {
  painelButtonOutlineClass,
  painelButtonPrimaryClass,
  painelTableShellClass,
} from '@/components/painel/painel-styles';
import { painelInputClass } from '@/components/painel/cms/painel-form';
import { GsImage } from '@/components/help-center/latest-power-shared';
import { ACE_ICON_SHELL_STYLE } from '@/components/help-center/aces/ace-shared';
import { useAcesCms } from '@/components/painel/aces/aces-cms-context';
import { usePainelSaveFeedback } from '@/components/painel/PainelSaveFeedback';
import { usePainelSession } from '@/components/painel/rbac/painel-session-context';
import { usePainelText } from '@/components/painel/painel-texts-context';
import { sessionHasPermission } from '@/lib/painel-rbac';
import { logPainelContentActivity } from '@/lib/painel-activity-log';
import {
  PUBLICATION_STATUS_BADGE_CLASS,
  PUBLICATION_STATUS_LABELS,
} from '@/lib/cms-publication';
import { cn } from '@/lib/utils';

function formatUpdatedAt(iso: string) {
  try {
    return new Intl.DateTimeFormat('pt-BR', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export function PainelAcesPage() {
  const { aces, hydrated, deleteAce, resetToMockSeed } = useAcesCms();
  const pageText = usePainelText('aces');
  const { showSaveSuccess } = usePainelSaveFeedback();
  const { session, role } = usePainelSession();
  const canCreateAces = sessionHasPermission(session, 'aces.create');
  const canDeleteAces = sessionHasPermission(session, 'aces.delete');
  const [search, setSearch] = useState('');
  const [deleteSlug, setDeleteSlug] = useState<string | null>(null);
  const [historyTarget, setHistoryTarget] = useState<{ id: string; label: string } | null>(null);
  const isSuperAdmin = role === 'super_admin';

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = [...aces].sort(
      (a, b) => new Date(b.releasedAt).getTime() - new Date(a.releasedAt).getTime()
    );
    if (!q) return list;
    return list.filter(
      (a) =>
        a.name.toLowerCase().includes(q) ||
        a.slug.toLowerCase().includes(q) ||
        String(a.powerId ?? '').includes(q)
    );
  }, [aces, search]);

  const deleteTarget = deleteSlug ? aces.find((a) => a.slug === deleteSlug) : null;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="font-inter text-[10px] font-bold uppercase tracking-[0.2em] text-sky-300/55">
            Content
          </p>
          <h1 className="mt-1 font-poppins text-2xl font-bold tracking-tight text-white md:text-3xl">
            {pageText.title}
          </h1>
          <p className="mt-1 font-inter text-sm text-slate-400">{pageText.description}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              resetToMockSeed();
              showSaveSuccess({
                title: 'Catálogo sincronizado',
                description: 'Os Aces foram repostos com os dados do repositório.',
              });
            }}
            className={painelButtonOutlineClass}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Sincronizar com repositório
          </Button>
          {canCreateAces ? (
            <Button asChild className={cn(painelButtonPrimaryClass, 'shadow-lg shadow-sky-950/30')}>
              <Link href="/painel/aces/new">
                <Plus className="mr-2 h-4 w-4" />
                Novo Ace
              </Link>
            </Button>
          ) : null}
        </div>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative max-w-md flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nome, slug ou ID…"
            className={cn(painelInputClass, 'pl-9')}
          />
        </div>
        <p className="font-inter text-xs text-slate-400">
          {hydrated ? `${filtered.length} aces` : 'Carregando…'}
        </p>
      </div>

      <div className={painelTableShellClass}>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[820px] border-collapse text-left font-inter text-sm">
            <thead>
              <tr className="border-b border-white/10 bg-white/[0.04]">
                <th className="px-4 py-3 font-poppins text-xs font-semibold uppercase tracking-wide text-sky-200/70">
                  Ace
                </th>
                <th className="px-4 py-3 font-poppins text-xs font-semibold uppercase tracking-wide text-sky-200/70">
                  ID
                </th>
                <th className="px-4 py-3 font-poppins text-xs font-semibold uppercase tracking-wide text-sky-200/70">
                  Preço
                </th>
                <th className="px-4 py-3 font-poppins text-xs font-semibold uppercase tracking-wide text-sky-200/70">
                  Status
                </th>
                <th className="px-4 py-3 font-poppins text-xs font-semibold uppercase tracking-wide text-sky-200/70">
                  Atualizado
                </th>
                <th className="px-4 py-3 text-right font-poppins text-xs font-semibold uppercase tracking-wide text-sky-200/70">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/[0.06]">
              {!hydrated ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-slate-400">
                    Carregando catálogo…
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-slate-400">
                    Nenhum Ace encontrado.
                  </td>
                </tr>
              ) : (
                filtered.map((ace) => (
                  <tr key={ace.slug} className="transition-colors hover:bg-white/[0.03]">
                    <td className="px-4 py-3">
                      <div className="flex min-w-0 items-center gap-3">
                        <span
                          className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-lg"
                          style={ACE_ICON_SHELL_STYLE}
                        >
                          <GsImage
                            code={ace.smileyCode}
                            className="flex h-full w-full items-center justify-center"
                            imgClassName="max-h-[26px] max-w-[26px] object-contain"
                            sizePx={40}
                          />
                        </span>
                        <div className="min-w-0">
                          <p className="truncate font-poppins font-medium text-blue-50">{ace.name}</p>
                          <p className="truncate font-mono text-xs text-slate-500">{ace.slug}</p>
                          {ace.publicationStatus && ace.publicationStatus !== 'published' ? (
                            <span
                              className={cn(
                                'mt-1 inline-flex rounded-full px-2 py-0.5 font-poppins text-[10px] font-semibold uppercase tracking-wide ring-1',
                                PUBLICATION_STATUS_BADGE_CLASS[ace.publicationStatus]
                              )}
                            >
                              {PUBLICATION_STATUS_LABELS[ace.publicationStatus]}
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 tabular-nums text-slate-300">
                      {ace.powerId ?? '—'}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-slate-300">
                      {ace.storePriceXats} xats
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={cn(
                          'inline-flex rounded-full px-2.5 py-0.5 font-poppins text-[11px] font-semibold uppercase tracking-wide',
                          ace.status === 'active'
                            ? 'bg-violet-500/15 text-violet-200 ring-1 ring-violet-400/25'
                            : ace.status === 'upcoming'
                              ? 'bg-sky-500/15 text-sky-200 ring-1 ring-sky-400/25'
                              : 'bg-slate-500/15 text-slate-300 ring-1 ring-white/10'
                        )}
                      >
                        {getAceStatusLabel(ace.status)}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-slate-400">
                      {formatUpdatedAt(ace.updatedAt)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-9 w-9 rounded-xl text-slate-300 hover:bg-white/10 hover:text-white"
                          asChild
                        >
                          <a
                            href={`/aces/${ace.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={`Ver ${ace.name} no site`}
                          >
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        </Button>
                        {isSuperAdmin ? (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-9 w-9 rounded-xl text-slate-300 hover:bg-white/10 hover:text-white"
                            onClick={() => setHistoryTarget({ id: ace.slug, label: ace.name })}
                            aria-label="Histórico de alterações"
                          >
                            <History className="h-4 w-4" />
                          </Button>
                        ) : null}
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-9 w-9 rounded-xl text-slate-300 hover:bg-white/10 hover:text-white"
                          asChild
                        >
                          <Link href={`/painel/aces/${encodeURIComponent(ace.slug)}/edit`}>
                            <Pencil className="h-4 w-4" />
                          </Link>
                        </Button>
                        {canDeleteAces ? (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-9 w-9 rounded-xl text-rose-300 hover:bg-rose-500/15 hover:text-rose-200"
                            onClick={() => setDeleteSlug(ace.slug)}
                            aria-label={`Excluir ${ace.name}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <p className="font-inter text-xs text-slate-400/70">
        Os Aces são sincronizados com a API. Itens publicados aparecem em /aces.
      </p>

      <PainelContentHistoryModal
        open={historyTarget !== null}
        onOpenChange={(open) => !open && setHistoryTarget(null)}
        resource="ace"
        resourceId={historyTarget?.id ?? null}
        resourceLabel={historyTarget?.label ?? ''}
      />

      <PainelConfirmDialog
        open={deleteSlug !== null}
        onOpenChange={(o) => !o && setDeleteSlug(null)}
        title={`Excluir ${deleteTarget?.name ?? 'Ace'}?`}
        description={
          deleteSlug
            ? `A página pública /aces/${deleteSlug} deixará de estar disponível no catálogo local.`
            : 'Esta ação não pode ser anulada.'
        }
        confirmLabel="Excluir"
        onConfirm={() => {
          if (deleteSlug) {
            deleteAce(deleteSlug);
            logPainelContentActivity(session, {
              action: 'delete',
              resource: 'ace',
              resourceId: deleteSlug,
              resourceLabel: deleteTarget?.name ?? deleteSlug,
            });
          }
          setDeleteSlug(null);
        }}
      />
    </div>
  );
}
